/**
 * Cloud Agent — per-column value formatting for `table` blocks. Pure and React-free so the
 * rules are testable in the node environment (the desktop table and the mobile card list both
 * render through these, which is what keeps the two views showing identical numbers).
 *
 * The server tags each column with a `type`; an absent or unknown type falls back to plain text,
 * so a table from a computation that has not been tagged yet renders exactly as it does today.
 */
import { currencyFormatter } from '../../../../../services/helperService';
import { getTableFormattedDate } from '../../../../../utils/dateUtils';
import type { TableCell, TableColumnType } from '../../../../../services/admin/cloudAgentTypes';

/** Shown for a null/undefined/empty cell — never an empty cell, which reads as a broken table. */
export const MISSING_VALUE = '—';

/** How many list entries to show inline before collapsing the rest into "+k more". */
export const LIST_PREVIEW_COUNT = 3;

/**
 * Locale is pinned to en-US to match `currencyFormatter` in helperService — mixing locales
 * would put "1,234.5" next to "€1.234,50" in the same row.
 */
const LOCALE = 'en-US';

/** Types whose values are numeric and therefore right-aligned. */
const NUMERIC_TYPES: readonly TableColumnType[] = [
  'number',
  'integer',
  'currency',
  'percent',
  'pp',
  'quantity',
];

export function isNumericColumnType(type?: TableColumnType): boolean {
  return !!type && NUMERIC_TYPES.indexOf(type) !== -1;
}

/** Right for numeric columns, left otherwise. An explicit server `align` always wins. */
export function alignForColumn(
  type?: TableColumnType,
  explicit?: 'left' | 'right'
): 'left' | 'right' {
  if (explicit) return explicit;
  return isNumericColumnType(type) ? 'right' : 'left';
}

function isMissing(value: TableCell | null | undefined): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * A cell's numeric value, or null when it is not actually a number.
 *
 * Deliberately does NOT coerce strings. A numeric column may legitimately carry a placeholder
 * ("—", "n/a") meaning "unknown", and coercing would turn that into NaN — or worse, turn `''`
 * into a confident `0`. Anything that is not a real number is passed through as written.
 */
function toNumber(value: TableCell): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

const decimals = (n: number, min: number, max: number) =>
  n.toLocaleString(LOCALE, { minimumFractionDigits: min, maximumFractionDigits: max });

/** Split a `list` cell into entries. Accepts a real array or the server's comma-joined string. */
export function listEntries(value: TableCell): string[] {
  if (Array.isArray(value)) return (value as unknown[]).map(String).filter(Boolean);
  if (isMissing(value)) return [];
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * A `list` cell: the first {@link LIST_PREVIEW_COUNT} entries inline, the remainder as a
 * "+k more" suffix, with the FULL list as the `title` so nothing is actually hidden.
 */
export function formatList(value: TableCell): { text: string; title: string } {
  const entries = listEntries(value);
  if (entries.length === 0) return { text: MISSING_VALUE, title: '' };
  const shown = entries.slice(0, LIST_PREVIEW_COUNT).join(', ');
  const rest = entries.length - LIST_PREVIEW_COUNT;
  return {
    text: rest > 0 ? `${shown} +${rest} more` : shown,
    title: entries.join(', '),
  };
}

/**
 * Format one cell for display. Always returns a string — a numeric type that receives a
 * non-numeric value falls back to the raw text rather than rendering "NaN".
 */
export function formatCell(value: TableCell, type?: TableColumnType): string {
  if (isMissing(value)) return MISSING_VALUE;

  switch (type) {
    case 'currency': {
      const n = toNumber(value);
      return n === null ? String(value) : currencyFormatter.format(n);
    }
    case 'integer': {
      const n = toNumber(value);
      return n === null ? String(value) : Math.round(n).toLocaleString(LOCALE);
    }
    case 'number':
    case 'quantity': {
      const n = toNumber(value);
      return n === null ? String(value) : decimals(n, 0, 2);
    }
    case 'percent': {
      // Values arrive already in 0-100 units — never rescale.
      const n = toNumber(value);
      return n === null ? String(value) : `${decimals(n, 1, 1)}%`;
    }
    case 'pp': {
      // Percentage POINTS, not percent: a cost-% moving 2 points is not "2%", so this must
      // never render a % sign. The sign is the whole point, so it is always explicit.
      const n = toNumber(value);
      if (n === null) return String(value);
      return `${n > 0 ? '+' : ''}${decimals(n, 1, 1)} pp`;
    }
    case 'date': {
      const parsed = new Date(String(value));
      return Number.isNaN(parsed.getTime()) ? String(value) : getTableFormattedDate(parsed);
    }
    case 'list':
      return formatList(value).text;
    case 'text':
    case 'category':
    default:
      return String(value);
  }
}
